import React, { useEffect, useState } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, BarChart3 } from "lucide-react";

const COLORS = ["#CFFF50", "#7B61FF", "#F472B6", "#22D3EE", "#FACC15", "#FB923C", "#34D399"];

export default function AdminAnalytics() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/admin/analytics").then(setData).catch(() => {}); }, []);

    return (
        <AppLayout searchPlaceholder="Search analytics...">
            <div className="mb-8">
                <span className="text-xs uppercase tracking-widest text-grape font-semibold">Insights</span>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Analytics</h1>
                <p className="text-sm text-gray-400 mt-2">Revenue, signups, top courses and category split.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-900 rounded-3xl border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-display font-bold text-xl flex items-center gap-2"><TrendingUp className="w-5 h-5 text-lime" /> Revenue (last 7 months)</h2>
                    </div>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data?.revenue || []}>
                                <defs>
                                    <linearGradient id="rev2" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#CFFF50" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#CFFF50" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="m" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
                                <Area type="monotone" dataKey="v" stroke="#CFFF50" strokeWidth={2.5} fill="url(#rev2)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-gray-900 rounded-3xl border border-white/5 p-6">
                    <h2 className="font-display font-bold text-xl flex items-center gap-2 mb-4"><BarChart3 className="w-5 h-5 text-grape" /> New signups (last 8 weeks)</h2>
                    <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data?.signups || []}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="w" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
                                <Bar dataKey="v" fill="#7B61FF" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-gray-900 rounded-3xl border border-white/5 p-6">
                    <h2 className="font-display font-bold text-xl mb-5">Top courses by enrollment</h2>
                    <div className="space-y-3">
                        {(data?.top_courses || []).map((c, i) => (
                            <div key={c.id} data-testid={`top-course-${i}`} className="flex items-center gap-4 bg-gray-800 rounded-2xl p-3">
                                <span className="w-8 h-8 rounded-xl bg-lime/15 text-lime font-bold text-sm flex items-center justify-center">{i + 1}</span>
                                {c.thumbnail && <img src={c.thumbnail} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{c.title}</div>
                                    <div className="text-xs text-gray-500">{c.category}</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display font-black text-lg">{(c.enrollments / 1000).toFixed(1)}k</div>
                                    <div className="text-xs text-gray-500">£{c.price}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-gray-900 rounded-3xl border border-white/5 p-6">
                    <h2 className="font-display font-bold text-xl mb-5">Category mix</h2>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={data?.categories || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} stroke="none">
                                    {(data?.categories || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                </Pie>
                                <Legend wrapperStyle={{ fontSize: 11, color: "#9CA3AF" }} />
                                <Tooltip contentStyle={{ background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}

